/**
 * ProductPicker - Search and add products to comparison
 *
 * Searchable dropdown that fetches products matching query.
 * Shows category, tier, and price in dropdown results.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useProductSearch } from '../hooks/useProductSearch';
import type { ProductSearchResult } from '../lib/api';

interface ProductPickerProps {
  /** Handler when product is selected */
  onSelect: (product: ProductSearchResult) => void;
  /** Product IDs already in comparison (to disable) */
  excludeIds?: Set<string>;
  /** Whether picker is disabled */
  disabled?: boolean;
  /** Placeholder text */
  placeholder?: string;
}

function getTierBadge(tier: 'budget' | 'mid' | 'premium') {
  switch (tier) {
    case 'budget':
      return (
        <span className="px-1.5 py-0.5 bg-green-500/10 text-green-400 text-xs rounded">
          Budget
        </span>
      );
    case 'mid':
      return (
        <span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-400 text-xs rounded">
          Mid
        </span>
      );
    case 'premium':
      return (
        <span className="px-1.5 py-0.5 bg-purple-500/10 text-purple-400 text-xs rounded">
          Premium
        </span>
      );
  }
}

export function ProductPicker({
  onSelect,
  excludeIds = new Set(),
  disabled = false,
  placeholder = 'Search products to compare...',
}: ProductPickerProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { products, isLoading, isEmpty, hasResults } = useProductSearch(query, {
    enabled: isOpen && query.length >= 2,
    limit: 10,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset highlight when products change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [products]);

  const handleSelect = useCallback(
    (product: ProductSearchResult) => {
      if (!excludeIds.has(product.id)) {
        onSelect(product);
        setQuery('');
        setIsOpen(false);
        inputRef.current?.blur();
      }
    },
    [excludeIds, onSelect]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || products.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < products.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : products.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < products.length) {
          handleSelect(products[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  return (
    <div className="relative">
      {/* Search Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          className="w-full px-4 py-2.5 pl-10 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:outline-none focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && query.length >= 2 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-neutral-900 border border-neutral-700 rounded-lg shadow-xl max-h-80 overflow-y-auto"
          role="listbox"
        >
          {isLoading && (
            <div className="p-4 text-center text-neutral-400">
              Searching...
            </div>
          )}

          {isEmpty && (
            <div className="p-4 text-center text-neutral-400">
              No products found for "{query}"
            </div>
          )}

          {hasResults && (
            <ul>
              {products.map((product, index) => {
                const isExcluded = excludeIds.has(product.id);
                const isHighlighted = index === highlightedIndex;

                return (
                  <li
                    key={product.id}
                    role="option"
                    aria-selected={isHighlighted}
                    aria-disabled={isExcluded}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelect(product)}
                      disabled={isExcluded}
                      className={`w-full p-3 text-left flex items-start gap-3 transition-colors ${
                        isExcluded
                          ? 'opacity-50 cursor-not-allowed'
                          : isHighlighted
                          ? 'bg-neutral-800'
                          : 'hover:bg-neutral-800'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-medium truncate">
                            {product.name}
                          </span>
                          {getTierBadge(product.tier)}
                          {isExcluded && (
                            <span className="px-1.5 py-0.5 bg-neutral-700 text-neutral-400 text-xs rounded">
                              Added
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-neutral-400">
                            {product.category_name}
                          </span>
                          <span className="font-mono text-neutral-300">
                            £{product.price_gbp}
                          </span>
                          <span className="font-mono text-neutral-500">
                            {product.weight_g}g
                          </span>
                        </div>
                      </div>
                      {!isExcluded && (
                        <svg
                          className="w-5 h-5 text-neutral-500 shrink-0 mt-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
